import { createPortal } from 'react-dom'
import { Bar } from 'react-chartjs-2'
import Chart from 'chart.js/auto'
import annotationPlugin from 'chartjs-plugin-annotation'
Chart.register(annotationPlugin)

import dayjs from 'dayjs'
import 'dayjs/locale/fr'

import { useEffect, useState, useMemo, useRef, useCallback } from 'react'

import { useAxiosApi } from '@/hooks/useAxiosApi'
import { Divider, Button, Modal, NumberInput, Select } from '@mantine/core'
import Box from '../ui/Box'
import classNames from 'classnames'
import { useBus } from 'react-bus'

const COLORS = [
    'rgb(57, 123, 236)',
    'rgb(80, 190, 120)',
    'rgb(255, 160, 50)',
    'rgb(170, 100, 220)',
    'rgb(240, 100, 100)',
    'rgb(100, 200, 220)',
]

export default function ComponentFlow({ component }) {
    const bus = useBus()
    const { requestGet, requestPatch } = useAxiosApi()
    const chartContainerRef = useRef(null)
    const [tooltipState, setTooltipState] = useState({ visible: false, x: 0, y: 0, align: 'center', title: '', items: [] })

    const externalTooltip = useCallback((context) => {
        const { chart, tooltip: model } = context
        if (model.opacity === 0) {
            setTooltipState((s) => ({ ...s, visible: false }))
            return
        }
        const canvasRect = chart.canvas.getBoundingClientRect()
        const items = model.dataPoints
            .filter((p) => p.parsed.y > 0)
            .map((p) => ({
                label: p.dataset.label,
                value: p.parsed.y,
                color: p.dataset.backgroundColor || p.dataset.borderColor,
                isLine: p.dataset.type === 'line',
            }))

        const x = canvasRect.left + model.caretX
        const align = x < window.innerWidth * 0.33 ? 'left' : x > window.innerWidth * 0.66 ? 'right' : 'center'

        setTooltipState({ visible: true, x, y: canvasRect.top + model.caretY, align, title: model.title[0] ?? '', items })
    }, [])

    const [dataFlow, setDataFlow] = useState([])

    useEffect(() => {
        let active = true
        setDataFlow([])
        setTooltipState((state) => ({ ...state, visible: false }))
        const load = async () => {
            const startDate = dayjs().subtract(24, 'month').format('YYYY-MM-DD')
            const endDate = dayjs().format('YYYY-MM-DD')
            const url = `/components/${component.id}/flow?date_start=${startDate}&date_end=${endDate}`
            const { data } = await requestGet(url)
            if (active) setDataFlow(Array.isArray(data) ? data : [])
        }
        load()
        return () => { active = false }
    }, [component.id])

    const monthlyData = useMemo(() => {
        if (!dataFlow.length) return null

        // Extract unique components with total consumption
        const componentsMap = {}
        const componentTotals = {}
        dataFlow.forEach((item) => {
            if (!componentsMap[item.component_id]) {
                componentsMap[item.component_id] = item.component_name
                componentTotals[item.component_id] = 0
            }
            componentTotals[item.component_id] += Number(item.sum)
        })
        const componentIds = Object.keys(componentsMap)

        // Build daily lookup per component
        const dailyByComponent = {}
        componentIds.forEach((cid) => { dailyByComponent[cid] = {} })
        dataFlow.forEach((item) => {
            dailyByComponent[item.component_id][item.date] = Number(item.sum)
        })

        // Group by model name (prefix before " - ") when there are more than 7 components
        const getModelName = (name) => name.split(' - ')[0].trim()
        const shouldGroup = componentIds.length > 7

        const groups = shouldGroup
            ? Object.entries(
                componentIds.reduce((acc, cid) => {
                    const model = getModelName(componentsMap[cid])
                    if (!acc[model]) acc[model] = { cids: [], total: 0 }
                    acc[model].cids.push(cid)
                    acc[model].total += componentTotals[cid]
                    return acc
                }, {})
              ).map(([model, { cids, total }]) => ({ id: model, label: model, total, cids }))
            : componentIds.map((cid) => ({
                id: cid,
                label: componentsMap[cid],
                total: componentTotals[cid],
                cids: [cid],
              }))

        // Sort groups by total consumption descending (biggest consumer at the bottom of the stack)
        groups.sort((a, b) => b.total - a.total)

        // Build daily lookup per group
        const dailyByGroup = {}
        groups.forEach((group) => {
            dailyByGroup[group.id] = {}
            group.cids.forEach((cid) => {
                Object.entries(dailyByComponent[cid]).forEach(([date, value]) => {
                    dailyByGroup[group.id][date] = (dailyByGroup[group.id][date] ?? 0) + value
                })
            })
        })

        // Build complete daily array over the 24-month fetch window (12 months buffer for MA365 + 12 months displayed)
        const start = dayjs().subtract(24, 'month').startOf('day')
        const end = dayjs().startOf('day')
        const allDays = []
        let cur = start
        while (cur.isBefore(end) || cur.isSame(end, 'day')) {
            const key = cur.format('YYYY-MM-DD')
            const totalForDay = groups.reduce((s, g) => s + (dailyByGroup[g.id][key] ?? 0), 0)
            allDays.push({ date: cur, key, total: totalForDay })
            cur = cur.add(1, 'day')
        }

        // 90-day and 365-day rolling means (running sum, O(n))
        let runningSum = 0
        let runningSum365 = 0
        const withMA = allDays.map((day, i) => {
            runningSum += day.total
            if (i >= 90) runningSum -= allDays[i - 90].total
            runningSum365 += day.total
            if (i >= 365) runningSum365 -= allDays[i - 365].total
            return {
                ...day,
                ma: runningSum / Math.min(i + 1, 90),
                ma365: runningSum365 / Math.min(i + 1, 365),
            }
        })

        // Aggregate last 12 months
        const currentMonth = dayjs().startOf('month')
        const twelveMonthsAgo = currentMonth.subtract(12, 'month')

        const monthlyByGroup = {}
        groups.forEach((g) => { monthlyByGroup[g.id] = {} })
        const monthlyTotal = {}
        const monthlyMA = {}
        const monthlyMA365 = {}

        withMA.forEach((day) => {
            if (day.date.isBefore(twelveMonthsAgo)) return
            const monthKey = day.date.format('YYYY-MM')

            groups.forEach((g) => {
                if (!monthlyByGroup[g.id][monthKey]) monthlyByGroup[g.id][monthKey] = 0
                monthlyByGroup[g.id][monthKey] += dailyByGroup[g.id][day.key] ?? 0
            })

            if (!monthlyTotal[monthKey]) monthlyTotal[monthKey] = 0
            monthlyTotal[monthKey] += day.total
            monthlyMA[monthKey] = day.ma
            monthlyMA365[monthKey] = day.ma365
        })

        const months = Object.keys(monthlyTotal).sort()
        if (!months.length) return null

        const labels = months.map((m) => dayjs(m).locale('fr').format('MMM YYYY'))

        const barDatasets = groups.map((g, i) => ({
            type: 'bar',
            label: g.label,
            data: months.map((m) => monthlyByGroup[g.id][m] ?? 0),
            backgroundColor: COLORS[i % COLORS.length],
            stack: 'stack',
            order: 2,
        }))

        const lineDataset90 = {
            type: 'line',
            label: 'Moyenne 3 mois',
            data: months.map((m) => {
                const [y, mo] = m.split('-').map(Number)
                return Math.round(monthlyMA[m] * new Date(y, mo, 0).getDate())
            }),
            borderColor: 'rgb(220, 80, 60)',
            backgroundColor: 'transparent',
            borderWidth: 2,
            pointRadius: 3,
            tension: 0.3,
            stack: 'ma90',
            order: 1,
        }

        const lineDataset365 = {
            type: 'line',
            label: 'Moyenne 12 mois',
            data: months.map((m) => {
                const [y, mo] = m.split('-').map(Number)
                return Math.round(monthlyMA365[m] * new Date(y, mo, 0).getDate())
            }),
            borderColor: 'rgb(99, 132, 255)',
            backgroundColor: 'transparent',
            borderWidth: 2,
            pointRadius: 3,
            tension: 0.3,
            borderDash: [5, 3],
            stack: 'ma365',
            order: 1,
        }

        return { labels, datasets: [...barDatasets, lineDataset90, lineDataset365] }
    }, [dataFlow, component.id])

    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {
            mode: 'nearest',
            intersect: true,
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                enabled: false,
                external: externalTooltip,
            },
            annotation: { annotations: {} },
        },
        scales: {
            x: { type: 'category', stacked: true },
            y: {
                beginAtZero: true,
                stacked: true,
                title: { display: true, text: 'unités / mois', font: { size: 11 } },
            },
        },
    }), [externalTooltip])

    // Flow rate display
    const flowModeNames = {
        auto_90: 'Auto 90 derniers jours',
        auto_60: 'Auto 60 derniers jours',
        fixed: 'Valeur fixée',
    }

    const flowRateMonthly = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(component.flow_rate * 30)
    const flowMode = flowModeNames[component.flow_mode] ?? 'Mode de calcul inconnu'
    const flowUpdatedAt = dayjs(component.flow_updated_at).format('ll à LT')

    const lastingDaysClassNames = classNames({
        'text-red-500': component.lasting_days < 90,
        'text-yellow-500': component.lasting_days >= 90 && component.lasting_days < 180,
        'text-green-500': component.lasting_days >= 180,
    })

    const refreshComponent = () => bus.emit('componentChanged')

    const [openedFlowModeModal, setOpenedFlowModeModal] = useState(false)
    const [newFlowMode, setNewFlowMode] = useState(component.flow_mode)
    const [newFlowRate, setNewFlowRate] = useState(component.flow_rate)

    useEffect(() => {
        setNewFlowMode(component.flow_mode)
        setNewFlowRate(component.flow_rate)
    }, [component.id])

    const openModal = () => {
        setOpenedFlowModeModal(true)
        setNewFlowMode(component.flow_mode)
        setNewFlowRate(component.flow_rate)
    }

    const saveFlow = async () => {
        await requestPatch(`/components/${component.id}`, {
            flow_mode: newFlowMode,
            flow_rate: newFlowRate,
        })
        await requestGet(`/components/${component.id}/calculate-flow`)
        setOpenedFlowModeModal(false)
        bus.emit('componentChanged')
    }

    if (!monthlyData && component.lasting_days == null && component.flow_mode == null) return null

    return (
        <Box title="Écoulement" subtitle="Basé sur les mouvements de stock de type préparations de commandes">
        <div className="flex flex-col gap-2">
            {component.lasting_days != null && (
                <>
                    <div className="flex flex-col mt-4 mb-4">
                        <span className="font-medium mb-4">Jours restants en stock</span>
                        <div className="flex items-center justify-between">
                            <span className={lastingDaysClassNames}>
                                {component.lasting_days} jour(s) restants
                            </span>
                            <Button variant="default" onClick={refreshComponent}>
                                Calculer maintenant
                            </Button>
                        </div>
                    </div>

                    <Divider variant="dashed" />
                </>
            )}

            {component.flow_mode != null && (
                <>
                    <div className="flex flex-col mt-4 mb-4">
                        <span className="font-medium mb-4">Calcul de l'écoulement</span>
                        <div className="flex items-center justify-between">
                            <div className="flex flex-col">
                                <span>{flowRateMonthly} unités par mois</span>
                                <span className="text-gray-400 text-sm">
                                    {flowMode} (MAJ : {flowUpdatedAt})
                                </span>
                            </div>
                            <Button variant="default" onClick={openModal}>
                                Modifier l&rsquo;écoulement
                            </Button>
                        </div>
                    </div>

                    <Divider variant="dashed" />
                </>
            )}

            <Modal
                title="Écoulement"
                onClose={() => setOpenedFlowModeModal(false)}
                opened={openedFlowModeModal}
                centered
            >
                <div className="flex flex-col gap-3">
                    <Select
                        value={newFlowMode}
                        onChange={(value) => setNewFlowMode(value)}
                        label="Mode de calcul"
                        data={[
                            { value: 'auto_90', label: '90 derniers jours' },
                            { value: 'fixed', label: 'Valeur fixe' },
                        ]}
                    />
                    {newFlowMode === 'fixed' && (
                        <NumberInput
                            value={newFlowRate}
                            onChange={(value) => setNewFlowRate(value)}
                            label="Valeur fixe"
                            suffix=" ventes par jour"
                            decimalScale={2}
                            fixedDecimalScale
                        />
                    )}
                    <Button className="mt-5" onClick={saveFlow}>
                        Enregistrer
                    </Button>
                </div>
            </Modal>

            {monthlyData && <div className="aspect-[3] relative" ref={chartContainerRef}>
                {monthlyData && (
                    <Bar
                        options={chartOptions}
                        data={{
                            labels: monthlyData.labels,
                            datasets: monthlyData.datasets,
                        }}
                    />
                )}
                {tooltipState.visible && createPortal(
                    <div
                        className="fixed pointer-events-none z-9999 bg-white rounded-xl shadow-xl border border-gray-100 py-3 px-4 text-sm"
                        style={{
                            left: tooltipState.x,
                            top: tooltipState.y,
                            transform: `translate(${tooltipState.align === 'left' ? '0' : tooltipState.align === 'right' ? '-100%' : '-50%'}, calc(-100% - 12px))`,
                            minWidth: '180px',
                        }}
                    >
                        <div className="text-gray-400 text-xs mb-1">{tooltipState.title}</div>
                        {tooltipState.items.map((item, i) => (
                            <div key={i} className="flex items-center gap-2">
                                {item.isLine ? (
                                    <div className="w-3 h-0.5 rounded-sm shrink-0" style={{ backgroundColor: item.color }} />
                                ) : (
                                    <div className="w-2 h-2 rounded-xs shrink-0" style={{ backgroundColor: item.color }} />
                                )}
                                <span className="text-gray-600 text-xs truncate">{item.label}</span>
                                <span className="font-semibold text-gray-900 ml-auto pl-4 shrink-0">{item.value}</span>
                            </div>
                        ))}
                    </div>,
                    document.body
                )}
            </div>}
        </div>
        </Box>
    )
}
